import StickyTableResponsive from '../../components/common/StickyTableResponsive';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import klubService from '../../services/klubService';
import authService from '../../services/authService';
import { goBackOr } from '../../utils/historyBack';
import { notify } from '../../components/common/AppToast';
import { ROUTES } from '../../config/routes';
import {
  FORMULE_LABELS, STATUT_LABELS, statutSelectOptions, MATCH_STATUS_LABELS, WORKFLOW_STATUS_LABELS,
  matchStatusBadgeClass, toInputDate,
} from '../../utils/klubUtils';
import {
  LIFECYCLE_PHASES, countStepProgress, groupStepsByPhase, phaseFromStatut,
} from '../../utils/klubPipelinePhases';
import CommercialSelect from '../../components/CommercialSelect';
import {
  AppAlert,
  AppButton,
  AppConfirmModal,
  AppFormField,
  AppInput,
  AppSelect,
  AppPillSwitch,
  AppSpinner,
  AppTextarea,
} from '../../components/ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import '../../styles/klub-fiche.scss';

function workflowResponsibleValue(step, commercialUserId) {
  if (step.use_adherent_commercial) {
    return commercialUserId ? String(commercialUserId) : '__commercial__';
  }
  return step.responsible_user_id ? String(step.responsible_user_id) : '';
}

function buildWorkflowDrafts(steps, commercialUserId) {
  const drafts = {};
  steps.forEach((step) => {
    drafts[step.step_code] = {
      status: step.status,
      responsible: workflowResponsibleValue(step, commercialUserId),
      completed_at: toInputDate(step.completed_at),
      note: step.note ?? '',
    };
  });
  return drafts;
}

function statutBadgeModifier(statut) {
  if (statut === 'resilie') return 'kf-badge--resilie';
  if (statut === 'actif') return '';
  return 'kf-badge--en-cours';
}
const WORKFLOW_STATUS_OPTIONS = ['pending', 'ok', 'na', 'blocked'];

function buildInfoForm(adherent) {
  return {
    entreprise: adherent.entreprise ?? '',
    formule: adherent.formule ?? 'total_klub',
    commercial_id: adherent.commercial?.id ?? '',
    statut: adherent.statut ?? 'actif',
    contact_nom: adherent.contact_nom ?? '',
    telephone: adherent.telephone ?? '',
    portable: adherent.portable ?? '',
    email: adherent.email ?? '',
    adresse: adherent.adresse ?? '',
    date_signature_devis: toInputDate(adherent.date_signature_devis),
    date_renouvellement: toInputDate(adherent.date_renouvellement),
    date_mise_en_ligne: toInputDate(adherent.date_mise_en_ligne),
    date_demande_resiliation: toInputDate(adherent.date_demande_resiliation),
    date_sortie_programmee: toInputDate(adherent.date_sortie_programmee),
    date_coupure: toInputDate(adherent.date_coupure),
    fiche_maj: !!adherent.fiche_maj,
    date_dernier_rdv: adherent.date_dernier_rdv ?? '',
    commentaire: adherent.commentaire ?? '',
    magazine_commercants_date: toInputDate(adherent.magazine_commercants_date),
    magazine_offre_date: toInputDate(adherent.magazine_offre_date),
  };
}

function buildCeForm(adherent) {
  const ce = adherent.ce ?? {};
  return {
    listing_donne: ce.listing_donne ?? '',
    nombre_salaries: ce.nombre_salaries ?? '',
    raison_refus: ce.raison_refus ?? '',
    acces_cartes: ce.acces_cartes ?? '',
    contact: ce.contact ?? '',
    telephone: ce.telephone ?? '',
    email: ce.email ?? '',
    ville: ce.ville ?? '',
    commercial_id: ce.commercial?.id ?? '',
  };
}

const KlubAdherentDetailPage = () => {
  const { ask, modal } = useAppConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const isSuperAdmin = authService.isSuperAdmin();
  const canArchive = isSuperAdmin || authService.isAdminCommercial();
  const [adherent, setAdherent] = useState(null);
  const [infoForm, setInfoForm] = useState(null);
  const [ceForm, setCeForm] = useState(null);
  const [commerciaux, setCommerciaux] = useState([]);
  const [klubUsers, setKlubUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [dolibarrQuery, setDolibarrQuery] = useState('');
  const [dolibarrSearchResults, setDolibarrSearchResults] = useState([]);
  const [dolibarrSearching, setDolibarrSearching] = useState(false);
  const [dolibarrOpen, setDolibarrOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openPhases, setOpenPhases] = useState(() => new Set());
  const [workflowDrafts, setWorkflowDrafts] = useState({});
  const [permanentDeleteOpen, setPermanentDeleteOpen] = useState(false);
  const [permanentDeleteTyped, setPermanentDeleteTyped] = useState('');

  const workflowSteps = useMemo(() => adherent?.workflow ?? [], [adherent?.workflow]);
  const commercialUserId = adherent?.commercial?.user_id ?? null;
  const parcoursUsers = useMemo(() => {
    const list = [...klubUsers];
    if (commercialUserId && !list.some((user) => user.id === commercialUserId)) {
      list.push({
        id: commercialUserId,
        full_name: adherent?.commercial?.user_name || 'Commercial',
      });
    }
    return list;
  }, [klubUsers, commercialUserId, adherent?.commercial?.user_name]);
  const currentPhase = adherent?.lifecycle_phase ?? phaseFromStatut(adherent?.statut);
  const workflowByPhase = useMemo(() => (
    groupStepsByPhase(workflowSteps.map((step) => ({ ...step, label: step.step_label })))
  ), [workflowSteps]);

  useEffect(() => {
    setWorkflowDrafts(buildWorkflowDrafts(workflowSteps, commercialUserId));
  }, [workflowSteps, commercialUserId]);

  useEffect(() => {
    if (currentPhase) {
      setOpenPhases(new Set([currentPhase]));
    }
  }, [currentPhase]);

  const togglePhase = (code) => {
    setOpenPhases((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const rankedSuggestions = useMemo(
    () => [...suggestions].sort((a, b) => (b.score ?? 0) - (a.score ?? 0)),
    [suggestions],
  );

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      klubService.getAdherent(id),
      klubService.getDolibarrSuggestions(id).catch(() => []),
      klubService.listCommerciaux().catch(() => []),
      klubService.listUsers().catch(() => []),
    ])
      .then(([data, sug, comm, users]) => {
        setAdherent(data);
        setInfoForm(buildInfoForm(data));
        setCeForm(buildCeForm(data));
        setSuggestions(sug);
        setCommerciaux(comm);
        setKlubUsers(users);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (loading || !adherent || window.location.hash !== '#dolibarr') return undefined;
    const node = document.getElementById('dolibarr');
    if (!node) return undefined;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return undefined;
  }, [loading, adherent]);

  const handleInfoChange = (field, value) => {
    setInfoForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetInfos = () => {
    if (adherent) {
      setInfoForm(buildInfoForm(adherent));
      setError(null);
    }
  };

  const saveInfos = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...infoForm,
        commercial_id: infoForm.commercial_id || null,
        commentaire: infoForm.commentaire?.trim() || null,
        date_signature_devis: infoForm.date_signature_devis || null,
        date_renouvellement: infoForm.date_renouvellement || null,
        date_mise_en_ligne: infoForm.date_mise_en_ligne || null,
        date_demande_resiliation: infoForm.date_demande_resiliation || null,
        date_sortie_programmee: infoForm.date_sortie_programmee || null,
        date_coupure: infoForm.date_coupure || null,
        magazine_commercants_date: infoForm.magazine_commercants_date || null,
        magazine_offre_date: infoForm.magazine_offre_date || null,
      };
      const updated = await klubService.updateAdherent(id, payload);
      setAdherent(updated);
      setInfoForm(buildInfoForm(updated));
      setCeForm(buildCeForm(updated));
      notify.success('Informations enregistrées');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveCe = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await klubService.updateAdherent(id, {
        ce: {
          ...ceForm,
          commercial_id: ceForm.commercial_id || null,
          nombre_salaries: ceForm.nombre_salaries === '' ? null : Number(ceForm.nombre_salaries),
          listing_donne: ceForm.listing_donne || null,
          acces_cartes: ceForm.acces_cartes || null,
        },
      });
      setAdherent(updated);
      setCeForm(buildCeForm(updated));
      notify.success('Fiche CE enregistrée');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveWorkflow = async () => {
    setSaving(true);
    setError(null);
    try {
      const steps = workflowSteps.map((step) => {
        const draft = workflowDrafts[step.step_code] ?? {};
        const payload = {
          step_code: step.step_code,
          status: draft.status,
          completed_at: draft.completed_at || null,
          note: draft.note || null,
        };
        if (draft.responsible === '__commercial__') {
          payload.use_adherent_commercial = true;
          payload.responsible_user_id = null;
        } else if (draft.responsible) {
          payload.use_adherent_commercial = false;
          payload.responsible_user_id = Number(draft.responsible);
        } else {
          payload.use_adherent_commercial = false;
          payload.responsible_user_id = null;
        }
        return payload;
      });
      const updated = await klubService.updateWorkflow(id, steps);
      setAdherent(updated);
      notify.success('Parcours enregistré');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const searchDolibarrManual = async () => {
    const query = dolibarrQuery.trim();
    if (query.length < 2) {
      setDolibarrSearchResults([]);
      return;
    }
    setDolibarrSearching(true);
    try {
      const rows = await klubService.searchDolibarrThirdparties(query);
      setDolibarrSearchResults(rows);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setDolibarrSearching(false);
    }
  };

  const handleDolibarrAction = async (action, thirdpartyId = null) => {
    setSaving(true);
    setError(null);
    try {
      const payload = { action };
      if (thirdpartyId) payload.dolibarr_thirdparty_id = thirdpartyId;
      const updated = await klubService.updateDolibarrLink(id, payload);
      setAdherent((prev) => ({ ...prev, ...updated }));
      load();
      notify.success('Liaison Dolibarr mise à jour');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    const ok = await ask({
      title: `Archiver l'adhérent « ${adherent.entreprise} » ?`,
      message: 'Il ne sera plus visible dans la liste. Les données pipeline, CE et liaison Dolibarr seront supprimées. Les avis seront conservés.',
      confirmLabel: 'Archiver',
      confirmVariant: 'danger',
    });
    if (!ok) return;

    setDeleting(true);
    setError(null);
    try {
      await klubService.deleteAdherent(id);
      navigate(ROUTES.klub.adherents);
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  const handlePermanentDelete = () => {
    setPermanentDeleteTyped('');
    setError(null);
    setPermanentDeleteOpen(true);
  };

  const confirmPermanentDelete = async () => {
    if (permanentDeleteTyped !== adherent.entreprise) {
      setError('Confirmation annulée : le nom ne correspond pas.');
      return;
    }
    setPermanentDeleteOpen(false);

    setDeleting(true);
    setError(null);
    try {
      await klubService.deleteAdherent(id, { permanent: true });
      navigate(ROUTES.klub.adherents);
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <MasterLayout>
        <AppSpinner />
      </MasterLayout>
    );
  }

  if (!adherent || !infoForm || !ceForm) {
    return (
      <MasterLayout>
        <AppAlert variant="danger">Adhérent introuvable</AppAlert>
      </MasterLayout>
    );
  }

  return (
    <>
    <MasterLayout>
      <div className="klub-fiche">
        <div className="kf-head">
          <div>
            <div className="kf-title-row">
              <h1 className="kf-title">{adherent.entreprise}</h1>
              {infoForm && (
                <label className="kf-toggle-row mb-0">
                  <span className="kf-switch">
                    <input
                      type="checkbox"
                      checked={infoForm.fiche_maj}
                      onChange={(e) => handleInfoChange('fiche_maj', e.target.checked)}
                    />
                    <span className="kf-slider" />
                  </span>
                  <span className="kf-toggle-lbl">En ligne</span>
                </label>
              )}
            </div>
            <div className="kf-badges">
              <span className={`kf-badge kf-badge--status ${statutBadgeModifier(adherent.statut)}`}>
                {STATUT_LABELS[adherent.statut]}
              </span>
              <span className="kf-badge kf-badge--plan">{FORMULE_LABELS[adherent.formule]}</span>
              {adherent.commercial && (
                <span className="kf-badge kf-badge--owner">{adherent.commercial.nom}</span>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {canArchive && (
              <button
                type="button"
                className="kf-archive-btn"
                disabled={deleting || saving}
                onClick={handleArchive}
              >
                {deleting ? 'Archivage…' : 'Archiver'}
              </button>
            )}
            {isSuperAdmin && (
              <button
                type="button"
                className="kf-archive-btn"
                disabled={deleting || saving}
                onClick={handlePermanentDelete}
              >
                Supprimer
              </button>
            )}
            <AppButton
              size="sm"
              variant="outline-secondary"
              icon="mdi:arrow-left"
              onClick={() => goBackOr(navigate, ROUTES.klub.dashboard)}
            >
              Retour
            </AppButton>
          </div>
        </div>

        {error && <div className="kf-alert kf-alert--error">{error}</div>}

          <FicheSection
            icon="mdi:office-building-outline"
            title="Identité & rattachement"
            subtitle="Entreprise, formule et suivi commercial"
          >
            <div className="kf-grid">
              <div className="kf-field kf-col-6">
                <label className="kf-label">Entreprise</label>
                <input className="kf-input" value={infoForm.entreprise} onChange={(e) => handleInfoChange('entreprise', e.target.value)} />
              </div>
              <div className="kf-field kf-col-3">
                <label className="kf-label">Formule</label>
                <select className="kf-select" value={infoForm.formule} onChange={(e) => handleInfoChange('formule', e.target.value)}>
                  {Object.entries(FORMULE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="kf-field kf-col-3">
                <label className="kf-label">Commercial</label>
                <CommercialSelect
                  value={infoForm.commercial_id}
                  onChange={(next) => handleInfoChange('commercial_id', next)}
                  commerciaux={commerciaux}
                  buttonClassName="kf-select text-start"
                />
              </div>
              <div className="kf-field kf-col-4">
                <label className="kf-label">Statut</label>
                <select className="kf-select" value={infoForm.statut} onChange={(e) => handleInfoChange('statut', e.target.value)}>
                  {statutSelectOptions(infoForm.statut).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div id="dolibarr" className="kf-doli">
              <div className="kf-doli__head">
                <div className="kf-doli__summary">
                  <div className="kf-label mb-0">Liaison Dolibarr</div>
                  <div className="kf-doli__meta">
                    <span className={`badge ${matchStatusBadgeClass(adherent.dolibarr_match_status)}`}>
                      {MATCH_STATUS_LABELS[adherent.dolibarr_match_status]}
                    </span>
                    <span>
                      {adherent.dolibarr_thirdparty_nom
                        ? `${adherent.dolibarr_thirdparty_nom} · ${adherent.dolibarr_thirdparty_id}`
                        : 'Aucun tiers lié'}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="kf-doli__btn"
                  aria-expanded={dolibarrOpen}
                  onClick={() => setDolibarrOpen((open) => !open)}
                >
                  {dolibarrOpen ? 'Replier' : 'Modifier'}
                </button>
              </div>
              {dolibarrOpen && (
              <>
              <div className="kf-doli__actions mt-2">
                <button type="button" className="kf-doli__btn" disabled={saving} onClick={() => handleDolibarrAction('rematch')}>
                  Re-matcher
                </button>
                {adherent.dolibarr_match_status === 'matched' && (
                  <>
                    <button type="button" className="kf-doli__btn kf-doli__btn--ok" disabled={saving} onClick={() => handleDolibarrAction('validate')}>
                      Confirmer
                    </button>
                    <button type="button" className="kf-doli__btn kf-doli__btn--danger" disabled={saving} onClick={() => handleDolibarrAction('reject')}>
                      Rejeter
                    </button>
                  </>
                )}
              </div>
              {adherent.dolibarr_match_status === 'rejected' && (
                <p className="kf-hint mb-0 mt-2">Liaison rejetée. Recherchez un autre tiers pour la rattacher.</p>
              )}
              <div className="kf-doli__search">
                <input
                  className="kf-input"
                  value={dolibarrQuery}
                  placeholder={adherent.dolibarr_match_status === 'rejected' ? 'Lier un autre tiers…' : 'Rechercher un tiers…'}
                  aria-label="Rechercher un tiers Dolibarr"
                  onChange={(e) => setDolibarrQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') searchDolibarrManual();
                  }}
                />
                <button type="button" className="kf-doli__btn" disabled={dolibarrSearching} onClick={searchDolibarrManual}>
                  {dolibarrSearching ? '…' : 'Rechercher'}
                </button>
              </div>
              {dolibarrSearchResults.length > 0 ? (
                <div className="kf-doli__list">
                  {dolibarrSearchResults.map((row) => (
                    <div key={row.id} className="kf-doli__row">
                      <span className="kf-doli__name">{row.nom}</span>
                      <button type="button" className="kf-doli__link" disabled={saving} onClick={() => handleDolibarrAction('validate', row.id)}>
                        Lier
                      </button>
                    </div>
                  ))}
                </div>
              ) : rankedSuggestions.length > 0 ? (
                <div className="kf-doli__list">
                  {rankedSuggestions.map((s) => (
                    <div key={s.id} className="kf-doli__row">
                      <span className="kf-doli__name">{s.nom}</span>
                      <span className="kf-doli__score">{s.score}%</span>
                      <button type="button" className="kf-doli__link" disabled={saving} onClick={() => handleDolibarrAction('validate', s.id)}>
                        Lier
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="kf-hint mb-0 mt-2">Aucune suggestion.</p>
              )}
              </>
              )}
            </div>
          </FicheSection>

          <FicheSection
            icon="mdi:phone-outline"
            title="Coordonnées"
            subtitle="Contact, téléphones, email et adresse"
          >
            <div className="kf-grid">
              <div className="kf-field kf-col-4">
                <label className="kf-label">Contact principal</label>
                <input className="kf-input" value={infoForm.contact_nom} onChange={(e) => handleInfoChange('contact_nom', e.target.value)} />
              </div>
              <div className="kf-field kf-col-4">
                <label className="kf-label">Téléphone fixe</label>
                <div className="kf-with-icon">
                  <Icon icon="mdi:phone-outline" className="kf-icon-prefix" />
                  <input className="kf-input" value={infoForm.telephone} onChange={(e) => handleInfoChange('telephone', e.target.value)} placeholder="—" />
                </div>
              </div>
              <div className="kf-field kf-col-4">
                <label className="kf-label">Portable</label>
                <div className="kf-with-icon">
                  <Icon icon="mdi:cellphone" className="kf-icon-prefix" />
                  <input className="kf-input" value={infoForm.portable} onChange={(e) => handleInfoChange('portable', e.target.value)} placeholder="—" />
                </div>
              </div>
              <div className="kf-field kf-col-4">
                <label className="kf-label">Email</label>
                <div className="kf-with-icon">
                  <Icon icon="mdi:email-outline" className="kf-icon-prefix" />
                  <input type="email" className="kf-input" value={infoForm.email} onChange={(e) => handleInfoChange('email', e.target.value)} placeholder="—" />
                </div>
              </div>
              <div className="kf-field kf-col-12">
                <label className="kf-label">Adresse</label>
                <textarea className="kf-textarea" rows={2} value={infoForm.adresse} onChange={(e) => handleInfoChange('adresse', e.target.value)} />
              </div>
            </div>
          </FicheSection>

          <FicheSection
            icon="mdi:calendar-month-outline"
            title="Suivi & échéances"
            subtitle="Contrat, mise en ligne, résiliation et dernier rendez-vous"
          >
            <div className="kf-grid">
              <div className="kf-field kf-col-3">
                <label className="kf-label">Date de signature</label>
                <input type="date" className="kf-input" value={infoForm.date_signature_devis} onChange={(e) => handleInfoChange('date_signature_devis', e.target.value)} />
              </div>
              <div className="kf-field kf-col-3">
                <label className="kf-label">Date de mise en ligne</label>
                <input type="date" className="kf-input" value={infoForm.date_mise_en_ligne} onChange={(e) => handleInfoChange('date_mise_en_ligne', e.target.value)} />
              </div>
              <div className="kf-field kf-col-3">
                <label className="kf-label">Date de renouvellement</label>
                <input type="date" className="kf-input" value={infoForm.date_renouvellement} onChange={(e) => handleInfoChange('date_renouvellement', e.target.value)} />
              </div>
              <div className="kf-field kf-col-3">
                <label className="kf-label">Date de demande de résiliation</label>
                <input type="date" className="kf-input" value={infoForm.date_demande_resiliation} onChange={(e) => handleInfoChange('date_demande_resiliation', e.target.value)} />
              </div>
              <div className="kf-field kf-col-3">
                <label className="kf-label">Date de résiliation programmée</label>
                <input type="date" className="kf-input" value={infoForm.date_sortie_programmee} onChange={(e) => handleInfoChange('date_sortie_programmee', e.target.value)} />
              </div>
              <div className="kf-field kf-col-3">
                <label className="kf-label">Date de coupure</label>
                <input type="date" className="kf-input" value={infoForm.date_coupure} onChange={(e) => handleInfoChange('date_coupure', e.target.value)} />
              </div>
              <div className="kf-field kf-col-3">
                <label className="kf-label">Date de dernier RDV</label>
                <input className="kf-input" value={infoForm.date_dernier_rdv} onChange={(e) => handleInfoChange('date_dernier_rdv', e.target.value)} placeholder="—" />
              </div>
              <div className="kf-field kf-col-12">
                <label className="kf-label">Commentaire</label>
                <textarea className="kf-textarea" rows={3} value={infoForm.commentaire} onChange={(e) => handleInfoChange('commentaire', e.target.value)} />
              </div>
            </div>
          </FicheSection>

          <FicheSection
            icon="mdi:book-open-page-variant-outline"
            title="Parution mag"
            subtitle="Dates de parution commerçants et offre"
          >
            <div className="kf-grid">
              <div className="kf-field kf-col-4">
                <label className="kf-label">Mag. commerçants</label>
                <input type="date" className="kf-input" value={infoForm.magazine_commercants_date} onChange={(e) => handleInfoChange('magazine_commercants_date', e.target.value)} />
              </div>
              <div className="kf-field kf-col-4">
                <label className="kf-label">Mag. offre</label>
                <input type="date" className="kf-input" value={infoForm.magazine_offre_date} onChange={(e) => handleInfoChange('magazine_offre_date', e.target.value)} />
              </div>
            </div>
          </FicheSection>

          <div className="kf-section" style={{ marginBottom: 40 }}>
            <div className="kf-actions">
              <button type="button" className="kf-btn kf-btn-secondary" disabled={saving} onClick={resetInfos}>
                Annuler
              </button>
              <button type="button" className="kf-btn kf-btn-success" disabled={saving} onClick={saveInfos}>
                {saving ? 'Enregistrement…' : 'Enregistrer les informations'}
              </button>
            </div>
          </div>

        <div className="kf-phase-stack">
          {LIFECYCLE_PHASES.map((phase) => {
            const steps = workflowByPhase[phase.code] ?? [];
            const { ok, total } = countStepProgress(steps);
            const isOpen = openPhases.has(phase.code);
            const isCurrent = currentPhase === phase.code;

            return (
              <div
                key={phase.code}
                className={`kf-phase-panel kf-phase-panel--${phase.code} ${isCurrent ? 'is-current' : ''}`}
              >
                <button
                  type="button"
                  className="kf-phase-panel__toggle"
                  onClick={() => togglePhase(phase.code)}
                  aria-expanded={isOpen}
                >
                  <span className="kf-phase-panel__title">
                    <Icon icon={isOpen ? 'mdi:chevron-down' : 'mdi:chevron-right'} />
                    {phase.label}
                    {isCurrent && <span className="kf-phase-panel__badge">Phase en cours</span>}
                  </span>
                  <span className="kf-phase-panel__count">
                    {total > 0 ? `${ok}/${total}` : '—'}
                  </span>
                </button>

                {isOpen && (
                  <div className="kf-phase-panel__body">
                    {steps.length === 0 ? (
                      <p className="text-secondary-light text-sm mb-0 px-3 py-4">
                        {phase.code === 'resiliation'
                          ? 'Pas encore de sous-étapes pipeline. Le suivi se fait via le statut et la date de coupure.'
                          : 'Aucune sous-étape sur cette phase.'}
                      </p>
                    ) : (
                      <StickyTableResponsive>
                        <table className="table mb-0 align-middle">
                          <thead>
                            <tr>
                              <th style={{ width: 48 }}>#</th>
                              <th>Sous-étape</th>
                              <th>Responsable</th>
                              <th>Statut</th>
                              <th>Date</th>
                              <th>Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {steps.map((step) => (
                              <WorkflowRow
                                key={step.step_code}
                                step={step}
                                users={parcoursUsers}
                                commercialUserId={commercialUserId}
                                draft={workflowDrafts[step.step_code]}
                                onChange={(next) => setWorkflowDrafts((prev) => ({
                                  ...prev,
                                  [step.step_code]: next,
                                }))}
                              />
                            ))}
                          </tbody>
                        </table>
                      </StickyTableResponsive>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div className="kf-section" style={{ marginBottom: 40 }}>
            <div className="kf-actions">
              <AppButton variant="primary" disabled={saving || workflowSteps.length === 0} onClick={saveWorkflow}>
                {saving ? 'Enregistrement…' : 'Enregistrer le parcours'}
              </AppButton>
            </div>
          </div>
        </div>

        <FicheSection
          icon="mdi:card-account-details-outline"
          title="Fiche CE"
          subtitle="Listing salariés, cartes et effectif"
        >
          <div className="kf-grid">
            <div className="kf-field kf-col-6">
              <label className="kf-label">Listing salariés</label>
              <AppPillSwitch
                ariaLabel="Listing salariés"
                value={ceForm.listing_donne === 'oui' ? 'oui' : (ceForm.listing_donne === 'refuse' ? 'refuse' : 'en_attente')}
                onChange={(value) => setCeForm({ ...ceForm, listing_donne: value })}
                options={[
                  ...(ceForm.listing_donne === 'refuse' ? [{ value: 'refuse', label: 'Refus' }] : []),
                  { value: 'oui', label: 'Reçu' },
                  { value: 'en_attente', label: 'En attente' },
                ]}
              />
            </div>
            <div className="kf-field kf-col-6">
              <label className="kf-label">Cartes délivrées</label>
              <AppPillSwitch
                ariaLabel="Cartes délivrées"
                value={ceForm.acces_cartes === 'livrees' || ceForm.acces_cartes === 'partiel' ? 'oui' : 'non'}
                onChange={(value) => {
                  if (value === 'oui') {
                    setCeForm({ ...ceForm, acces_cartes: 'livrees' });
                    return;
                  }
                  if (ceForm.acces_cartes === 'non_livrees') return;
                  setCeForm({ ...ceForm, acces_cartes: 'en_attente' });
                }}
                options={[
                  { value: 'oui', label: 'Oui' },
                  { value: 'non', label: 'Non' },
                ]}
              />
            </div>
            <div className="kf-field kf-col-4">
              <label className="kf-label">Nombre de salariés</label>
              <AppInput
                type="number"
                min="0"
                value={ceForm.nombre_salaries}
                onChange={(e) => setCeForm({ ...ceForm, nombre_salaries: e.target.value })}
              />
            </div>
            <div className="kf-field kf-col-6">
              <label className="kf-label">Email</label>
              <AppInput value={ceForm.email} onChange={(e) => setCeForm({ ...ceForm, email: e.target.value })} />
            </div>
            <div className="kf-field kf-col-12">
              <label className="kf-label">Raison du refus</label>
              <AppTextarea rows={2} value={ceForm.raison_refus} onChange={(e) => setCeForm({ ...ceForm, raison_refus: e.target.value })} />
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-3">
            <AppButton variant="success" loading={saving} loadingLabel="Enregistrement…" onClick={saveCe}>
              Enregistrer la fiche CE
            </AppButton>
            <AppButton
              as={Link}
              to={ROUTES.klub.adherentSalaries(id)}
              variant="outline-primary"
              icon="mdi:account-group-outline"
            >
              Liste des salariés
            </AppButton>
          </div>
        </FicheSection>

      </div>
    </MasterLayout>
    {modal}
    <AppConfirmModal
      show={permanentDeleteOpen}
      title={`Supprimer définitivement « ${adherent.entreprise} » ?`}
      confirmLabel="Supprimer définitivement"
      confirmVariant="danger"
      confirmIcon="mdi:delete-outline"
      saving={deleting}
      onClose={() => {
        if (deleting) return;
        setPermanentDeleteOpen(false);
        setPermanentDeleteTyped('');
      }}
      onConfirm={confirmPermanentDelete}
    >
      <p>
        Cette action est irréversible. Les avis seront conservés sans lien adhérent.
      </p>
      <AppFormField
        label={`Tapez exactement le nom de l’entreprise : ${adherent.entreprise}`}
        className="mb-0"
      >
        <AppInput
          value={permanentDeleteTyped}
          onChange={(e) => setPermanentDeleteTyped(e.target.value)}
          placeholder={adherent.entreprise}
          autoComplete="off"
          disabled={deleting}
        />
      </AppFormField>
      {permanentDeleteTyped && permanentDeleteTyped !== adherent.entreprise ? (
        <p className="text-danger small mb-0 mt-2">Le nom ne correspond pas.</p>
      ) : null}
    </AppConfirmModal>
    </>
  );
};

function FicheSection({ icon, title, subtitle, children }) {
  return (
    <div className="kf-section">
      <div className="kf-section-head">
        <div className="kf-section-icon">
          <Icon icon={icon} />
        </div>
        <div>
          <h5 className="kf-section-title">{title}</h5>
          <p className="kf-section-sub">{subtitle}</p>
        </div>
      </div>
      <div className="kf-section-body">{children}</div>
    </div>
  );
}

function WorkflowRow({ step, users, commercialUserId, draft, onChange }) {
  const value = draft ?? {
    status: step.status,
    responsible: workflowResponsibleValue(step, commercialUserId),
    completed_at: toInputDate(step.completed_at),
    note: step.note ?? '',
  };

  return (
    <tr>
      <td>{step.step_order}</td>
      <td>
        <div className="fw-medium">{step.step_label}</div>
        {step.description && (
          <small className="text-secondary-light d-block">{step.description}</small>
        )}
      </td>
      <td>
        <AppSelect
          size="sm"
          style={{ minWidth: 180 }}
          value={value.responsible}
          onChange={(e) => onChange({ ...value, responsible: e.target.value })}
        >
          <option value="">— Non assigné —</option>
          {commercialUserId ? null : <option value="__commercial__">Commercial du client</option>}
          {users.map((u) => (
            <option key={u.id} value={String(u.id)}>{u.full_name}</option>
          ))}
        </AppSelect>
      </td>
      <td>
        <AppSelect size="sm" style={{ minWidth: 120 }} value={value.status} onChange={(e) => onChange({ ...value, status: e.target.value })}>
          {WORKFLOW_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{WORKFLOW_STATUS_LABELS[s]}</option>)}
        </AppSelect>
      </td>
      <td>
        <AppInput type="date" size="sm" value={value.completed_at} onChange={(e) => onChange({ ...value, completed_at: e.target.value })} />
      </td>
      <td>
        <AppInput size="sm" value={value.note} onChange={(e) => onChange({ ...value, note: e.target.value })} />
      </td>
    </tr>
  );
}

export default KlubAdherentDetailPage;
